import { useEffect, useRef, lazy, Suspense } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { SidebarProvider } from "@/components/ui/sidebar";
import { useMinWidth } from "@/hooks/useMinWidth";
import BottomNavigation from "@/components/BottomNavigation";
import { ConsumerLayoutContext } from "@/contexts/ConsumerLayoutContext";
import AppErrorBoundary from "@/components/AppErrorBoundary";
import LegalFooterLinks from "@/components/LegalFooterLinks";

const AppSidebar = lazy(() => import("@/components/AppSidebar").then((m) => ({ default: m.AppSidebar })));

function getActiveTab(location: string): string {
  if (location === "/") return "feed";
  if (
    location.startsWith("/restaurants") ||
    location.startsWith("/restaurant/")
  )
    return "menus";
  if (location.startsWith("/friends")) return "friends";
  if (location.startsWith("/my-eatzy")) return "myeatzy";
  if (location.startsWith("/profile")) return "profile";
  return "feed";
}

function tabToPath(tab: string): string {
  switch (tab) {
    case "feed":
      return "/";
    case "menus":
      return "/restaurants";
    case "friends":
      return "/friends";
    case "myeatzy":
      return "/my-eatzy";
    case "profile":
      return "/profile";
    default:
      return "/";
  }
}

interface ConsumerLayoutProps {
  children: React.ReactNode;
}

export default function ConsumerLayout({ children }: ConsumerLayoutProps) {
  const showDesktopSidebar = useMinWidth(768);
  const [location, setLocation] = useLocation();
  const activeTab = getActiveTab(location);
  const { isAuthenticated } = useAuth();
  const initialLocation = useRef(location);
  const redirectedRef = useRef(initialLocation.current !== "/");

  const { data: prefsData } = useQuery<{
    myEatzyConfig: { homeTab?: string } | null;
  }>({
    queryKey: ["/api/user/preferences"],
    enabled: isAuthenticated,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (redirectedRef.current) return;
    if (!isAuthenticated) return;
    if (location !== "/") return;
    const homeTab = prefsData?.myEatzyConfig?.homeTab;
    if (homeTab && homeTab !== "feed") {
      redirectedRef.current = true;
      setLocation(tabToPath(homeTab));
    }
  }, [isAuthenticated, location, prefsData, setLocation]);

  const handleTabChange = (tab: string) => {
    redirectedRef.current = true;
    const homeTab = prefsData?.myEatzyConfig?.homeTab;
    const resolvedTab =
      tab === "feed" && homeTab && homeTab !== "feed" ? homeTab : tab;
    setLocation(tabToPath(resolvedTab));
  };

  const handleCreateClick = () => {
    if (location !== "/") {
      setLocation("/");
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("kudoz:open-create-review"));
      }, 80);
    } else {
      window.dispatchEvent(new CustomEvent("kudoz:open-create-review"));
    }
  };

  return (
    <ConsumerLayoutContext.Provider value={true}>
      <SidebarProvider
        style={
          {
            "--sidebar-width": "14rem",
            "--sidebar-width-icon": "3.5rem",
          } as React.CSSProperties
        }
      >
        <div className="flex h-screen pb-16 md:pb-0 w-full overflow-hidden">
          {showDesktopSidebar && (
            <div className="hidden md:flex">
              <Suspense fallback={null}><AppSidebar onCreateClick={handleCreateClick} /></Suspense>
            </div>
          )}
          <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
            <div className="flex flex-col flex-1 min-h-0 w-full md:max-w-md md:mx-auto lg:max-w-5xl lg:mx-auto overflow-y-auto">
              <AppErrorBoundary>{children}</AppErrorBoundary>
            </div>
            <div className="md:hidden flex justify-center px-4 py-1.5 border-t border-border/50 shrink-0">
              <LegalFooterLinks />
            </div>
          </div>
        </div>
        <BottomNavigation
          className="md:hidden"
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
      </SidebarProvider>
    </ConsumerLayoutContext.Provider>
  );
}
